/**
 * The five config files the export step writes, as strings.
 *
 * Split out of ExportStep.tsx: this is text generation over the configure
 * store, with no UI of its own, and it is what the export callback actually
 * needs a stable handle on.
 */

import { useCallback } from "react";
import { useConfigureStore } from "./ConfigureStore";

export function useConfigGenerators() {
  const {
    servoMappings,
    planningGroups,
    controllers,
    disabledCollisionPairs,
    gazeboPhysics,
    gazeboPlugins,
    ilObservation,
    ilAction,
  } = useConfigureStore();

  const generateServoYaml = useCallback(() => {
    const mappings = servoMappings.filter((m) => m.servoId !== null);
    return `# Servo Mapping Configuration
# Generated by Robot Compiler

servo_mapping:
${mappings
  .map(
    (m) => `  ${m.jointName}:
    servo_id: ${m.servoId}
    direction: ${m.direction}
    angle_offset: ${m.angleOffset}`
  )
  .join("\n")}`;
  }, [servoMappings]);

  const generateSRDF = useCallback(() => {
    return `<?xml version="1.0" encoding="UTF-8"?>
<!-- SRDF Generated by Robot Compiler -->
<robot name="robot">
${planningGroups
  .map(
    (g) => `  <group name="${g.name}">
${g.joints.map((j) => `    <joint name="${j}" />`).join("\n")}
  </group>`
  )
  .join("\n")}

${planningGroups
  .filter((g) => g.endEffectorLink)
  .map(
    (g) => `  <end_effector name="${g.name}_eef" parent_link="${g.endEffectorLink}" group="${g.name}" />`
  )
  .join("\n")}

  <!-- Disabled Collisions -->
${disabledCollisionPairs
  .map(([l1, l2]) => `  <disable_collisions link1="${l1}" link2="${l2}" reason="User defined" />`)
  .join("\n")}
</robot>`;
  }, [planningGroups, disabledCollisionPairs]);

  const generateControllersYaml = useCallback(() => {
    return `# ros2_control Configuration
# Generated by Robot Compiler

controller_manager:
  ros__parameters:
    update_rate: 100

${controllers
  .map(
    (c) => `${c.name}:
  ros__parameters:
    type: ${c.type === "joint_trajectory_controller" ? "joint_trajectory_controller/JointTrajectoryController" : c.type === "gripper_controller" ? "gripper_action_controller/GripperActionController" : "position_controllers/JointGroupPositionController"}
    joints:
${c.joints.map((j) => `      - ${j}`).join("\n")}`
  )
  .join("\n\n")}`;
  }, [controllers]);

  const generateGazeboYaml = useCallback(() => {
    const enabledPlugins = gazeboPlugins.filter((p) => p.enabled);
    return `# Gazebo Configuration
# Generated by Robot Compiler

physics:
  solver: ${gazeboPhysics.solver}
  step_size: ${gazeboPhysics.stepSize}
  real_time_factor: ${gazeboPhysics.realTimeFactor}

plugins:
${enabledPlugins
  .map(
    (p) => `  - name: ${p.name}
    filename: ${p.filename}${
      Object.keys(p.parameters).length > 0
        ? `
    parameters:
${Object.entries(p.parameters)
  .map(([k, v]) => `      ${k}: ${v}`)
  .join("\n")}`
        : ""
    }`
  )
  .join("\n")}`;
  }, [gazeboPhysics, gazeboPlugins]);

  const generateILYaml = useCallback(() => {
    return `# Imitation Learning Configuration
# Generated by Robot Compiler

observation:
  include_joint_positions: ${ilObservation.includeJointPositions}
  include_joint_velocities: ${ilObservation.includeJointVelocities}
  include_end_effector_pose: ${ilObservation.includeEndEffectorPose}
${
  ilObservation.customObservations.length > 0
    ? `  custom_observations:
${ilObservation.customObservations.map((o) => `    - ${o}`).join("\n")}`
    : ""
}

action:
  type: ${ilAction.actionType}
  scale: ${ilAction.actionScale}
  joints:
${ilAction.selectedJoints.map((j) => `    - ${j}`).join("\n")}`;
  }, [ilObservation, ilAction]);

  return {
    generateServoYaml,
    generateSRDF,
    generateControllersYaml,
    generateGazeboYaml,
    generateILYaml,
  };
}
